import { useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * useSwipeGesture
 * Enables swipe-back (right swipe) / swipe-forward (left swipe) navigation
 * on touch devices. Only activates on mobile (touch-capable).
 */
export function useSwipeGesture({ onSwipeLeft, onSwipeRight, threshold = 60 } = {}) {
  const startX = useRef(null);
  const startY = useRef(null);

  const handleTouchStart = useCallback((e) => {
    startX.current = e.touches[0].clientX;
    startY.current = e.touches[0].clientY;
  }, []);

  const handleTouchEnd = useCallback((e) => {
    if (startX.current === null) return;
    const dx = e.changedTouches[0].clientX - startX.current;
    const dy = e.changedTouches[0].clientY - (startY.current || 0);
    // Only trigger if horizontal swipe is dominant
    if (Math.abs(dx) < threshold || Math.abs(dx) < Math.abs(dy) * 1.5) return;
    if (dx > 0 && onSwipeRight) onSwipeRight();
    if (dx < 0 && onSwipeLeft) onSwipeLeft();
    startX.current = null;
    startY.current = null;
  }, [onSwipeLeft, onSwipeRight, threshold]);

  useEffect(() => {
    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('touchend', handleTouchEnd, { passive: true });
    return () => {
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleTouchStart, handleTouchEnd]);
}

/**
 * useSwipeNavigation
 * Convenience hook that wires swipe gestures to browser history navigation.
 * Only active on touch devices (pointer: coarse).
 */
export function useSwipeNavigation() {
  const navigate = useNavigate();

  const isTouchDevice = typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches;

  useSwipeGesture({
    onSwipeRight: isTouchDevice ? () => navigate(-1) : undefined,
    threshold: 80,
  });
}

export default useSwipeGesture;
